import { useState } from 'react';
import { KeyValue, UnicodeEntity } from '@mews/enums';
import { Field, FieldProps } from '../Field';
import { TagInput } from './TagInput';

const FieldTagInput = Field(TagInput);

export interface TagInputFieldProps extends FieldProps {
    delimiters: UnicodeEntity[];
    onChange: (e: React.ChangeEvent<HTMLInputElement> | React.SyntheticEvent, value: string[]) => void;
    value: string[];
    invalidTags?: string[];
}

export const TagInputField = ({
    delimiters,
    onChange,
    error,
    invalidTags,
    value: tags,
    ...other
}: TagInputFieldProps) => {
    const [inputValue, setInputValue] = useState('');

    const delimitersPattern = new RegExp(delimiters.join('|'), 'g');

    const handleRemoveTag = (event: React.SyntheticEvent, value: string | number | boolean) => {
        const newTags = tags.filter((tag) => tag !== value);

        onChange(event, newTags);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === KeyValue.Backspace && !inputValue && tags.length > 0) {
            onChange(event, tags.slice(0, -1));
        }
    };

    const parseValueIntoTags = (value: string) => [
        ...new Set(
            value.split(delimitersPattern).filter((newTag) => Boolean(newTag) && !tags.some((tag) => tag === newTag))
        ),
    ];

    const handleParseValue = (
        event: React.ChangeEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement>,
        value: string
    ) => {
        const newTags = parseValueIntoTags(value);

        if (newTags.length > 0) {
            const finalTags = [...tags, ...newTags];

            onChange(event, finalTags);
        }

        setInputValue('');
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;

        setInputValue(value);

        if (delimitersPattern.test(value)) {
            handleParseValue(event, value);
        }
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        if (inputValue) {
            handleParseValue(event, inputValue);
        }
    };

    return (
        <FieldTagInput
            tags={tags}
            value={inputValue}
            handleRemoveTag={handleRemoveTag}
            handleKeyDown={handleKeyDown}
            handleChange={handleChange}
            handleBlur={handleBlur}
            error={error}
            invalidTags={invalidTags}
            {...other}
        />
    );
};
